import React, { useEffect, useRef } from 'react';

type Star = {
    x: number;
    y: number;
    speed: number;
    radius: number;
    color: string;
};

type Props = {
    children?: React.ReactNode;
    /** Star colors. Use rgba() with low alpha for subtle backgrounds. */
    colors?: string[];
    /** Number of stars. Default 140. */
    starCount?: number;
    /** Per-star speed in px/frame is randomized in [min, max]. Defaults tuned for a slow, subtle drift. */
    speedRange?: [number, number];
};

/**
 * Continuously-animating starfield background. Fills its parent and renders
 * children centered on top. Inspired by HyperspaceOverlay but standalone:
 * no hover gating, no shared state with the button overlay.
 */
const HyperspaceBackground: React.FC<Props> = ({
    children,
    colors,
    starCount = 140,
    speedRange = [0.1, 0.8],
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const starsRef = useRef<Star[]>([]);
    const rafRef = useRef<number | null>(null);
    const dimsRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });

    const palette = colors ?? [
        'rgba(255,255,255,0.45)',
        'rgba(242,113,204,0.35)',
        'rgba(109,109,220,0.35)',
    ];
    const paletteRef = useRef(palette);
    paletteRef.current = palette;

    useEffect(() => {
        const wrapper = wrapperRef.current;
        const canvas = canvasRef.current;
        if (!wrapper || !canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const [minSpeed, maxSpeed] = speedRange;
        const speedSpan = maxSpeed - minSpeed;

        const initStars = (w: number, h: number) => {
            const p = paletteRef.current;
            starsRef.current = Array.from({ length: starCount }, () => ({
                x: Math.random() * w,
                y: Math.random() * h,
                speed: minSpeed + Math.random() * speedSpan,
                radius: 0.5 + Math.random() * 1.6,
                color: p[Math.floor(Math.random() * p.length)],
            }));
        };

        const handleResize = () => {
            const rect = wrapper.getBoundingClientRect();
            const w = Math.max(0, rect.width);
            const h = Math.max(0, rect.height);
            if (w === 0 || h === 0) return;
            canvas.width = w;
            canvas.height = h;
            dimsRef.current = { w, h };
            initStars(w, h);
        };

        const tick = () => {
            const { w, h } = dimsRef.current;
            ctx.clearRect(0, 0, w, h);
            for (const star of starsRef.current) {
                star.x -= star.speed;
                if (star.x < -2) {
                    star.x = w + 2;
                    star.y = Math.random() * h;
                }
                ctx.beginPath();
                ctx.fillStyle = star.color;
                ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
                ctx.fill();
            }
            rafRef.current = requestAnimationFrame(tick);
        };

        handleResize();
        const ro = new ResizeObserver(handleResize);
        ro.observe(wrapper);
        rafRef.current = requestAnimationFrame(tick);

        return () => {
            ro.disconnect();
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [starCount, speedRange]);

    return (
        <div
            ref={wrapperRef}
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                overflow: 'hidden',
            }}
        >
            <canvas
                ref={canvasRef}
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 0,
                }}
            />
            <div
                style={{
                    position: 'relative',
                    zIndex: 1,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {children}
            </div>
        </div>
    );
};

export default HyperspaceBackground;
